import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { fetchOpenWork } from "../lib/openWork";
import { TONES } from "../lib/workflows";

/* Global "My open work" ribbon. Slim, sticky under the top nav on
   every authenticated portal page. Pulls items from the role-scoped
   `/<role>-portal/open-work` endpoint and surfaces only the ones whose
   count > 0. Each item links to the deep route that resolves it.

   Hidden when: user is anonymous, or all items are zero (shows a
   subtle "All clear" pill so the affordance is still discoverable). */
export default function OpenWorkBar() {
  const { user, role, isAnonymous } = useRole();
  const { pathname } = useLocation();
  const [items, setItems] = useState(null);

  // City Staff is observational; the chips reflect platform state, not
  // their inbox. Other roles use the actionable framing.
  const ribbonLabel =
    role === "city_staff" ? "Awaiting decisions in Accela" : "My open work";
  const clearLabel =
    role === "city_staff"
      ? "All clear — no items currently in flight."
      : "All clear — no items need your attention right now.";

  // Re-fetch on every authenticated route change so action counts
  // reflect the action the user just took (e.g., approved an app).
  useEffect(() => {
    if (isAnonymous || !user?.id) {
      setItems(null);
      return;
    }
    let alive = true;
    fetchOpenWork(role, user.id)
      .then((d) => alive && setItems(d?.items || []))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [user?.id, role, isAnonymous, pathname]);

  if (isAnonymous || items == null) return null;

  const open = items.filter((i) => i.count > 0);

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        backgroundColor: "#FBFAFD",
        borderBottom: "1px solid #ECEBF1",
        flexShrink: 0,
      }}
      data-testid="open-work-bar"
    >
      <div
        className="flex items-center flex-wrap"
        style={{
          gap: 16,
          padding: "8px 24px",
          minHeight: 40,
        }}
      >
        <span
          className="wn-label"
          style={{
            color: "#6C6B85",
            letterSpacing: "0.10em",
            flexShrink: 0,
          }}
          data-testid="open-work-label"
        >
          {ribbonLabel}
        </span>

        {open.length === 0 ? (
          <span
            className="inline-flex items-center"
            style={{
              gap: 6,
              color: "#4F6149",
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
            data-testid="open-work-clear"
          >
            <CheckCircle2 size={14} strokeWidth={2.2} />
            {clearLabel}
          </span>
        ) : (
          <div
            className="flex items-center flex-wrap"
            style={{ gap: 6, flex: 1, minWidth: 0 }}
          >
            {open.map((it, i) => (
              <React.Fragment key={it.key}>
                <OpenWorkChip item={it} />
                {i < open.length - 1 && (
                  <span
                    aria-hidden
                    style={{
                      color: "#9AA0C0",
                      fontSize: 12,
                      lineHeight: 1,
                    }}
                  >
                    ·
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OpenWorkChip({ item }) {
  const tone = TONES[item.tone] || TONES.indigo;
  return (
    <Link
      to={item.href}
      className="inline-flex items-center transition-colors"
      style={{
        gap: 6,
        padding: "4px 10px 4px 8px",
        borderRadius: 999,
        backgroundColor: tone.soft,
        color: tone.bg,
        fontSize: 12.5,
        fontWeight: 600,
        textDecoration: "none",
        fontFamily: '"Nunito Sans", sans-serif',
        whiteSpace: "nowrap",
      }}
      data-testid={`open-work-chip-${item.key}`}
    >
      <span
        className="tabular-nums"
        style={{
          minWidth: 18,
          height: 18,
          padding: "0 5px",
          borderRadius: 999,
          backgroundColor: tone.bg,
          color: tone.fg,
          fontSize: 11,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.count}
      </span>
      <span>{item.label}</span>
      <ArrowRight size={12} strokeWidth={2} style={{ opacity: 0.6 }} />
    </Link>
  );
}
